/**
 * Friends Collection Gift Handler
 * Auto-adds free keychain gift to cart for first 500 purchases
 *
 * To disable this JS behavior (so you can use an external app/plugin like Cartbot
 * to manage gift adds) set `window.DISABLE_FRIEND_GIFT_HANDLER = true` in your
 * `layout/theme.liquid` (before this asset is loaded) or add the following
 * snippet into a script tag in the header:
 *
 * <script>window.DISABLE_FRIEND_GIFT_HANDLER = true;</script>
 *
 * When disabled, this file will not define the `friend-gift-handler` custom
 * element and none of the auto-add/monitoring logic will run. You can then
 * use CSS to hide/disable quantity controls for the gift variant if needed.
 */

// Respect explicit disable flag to allow using a plugin/app instead of this handler
if (window.DISABLE_FRIEND_GIFT_HANDLER) {
    console.log('FriendGiftHandler is disabled via window.DISABLE_FRIEND_GIFT_HANDLER');
} else {

    if (!customElements.get('friend-gift-handler')) {
        customElements.define(
            'friend-gift-handler',
            class FriendGiftHandler extends HTMLElement {
                constructor() {
                    super();
                    // Variant id can be provided directly via `data-keychain-variant-id`.
                    // Also accept a few alternative dataset keys if present.
                    this.keychainVariantId = (this.dataset.keychainVariantId || this.dataset.keychainVariant || this.dataset.variantId || this.dataset.variant || null);
                    // Optionally allow SKU to be provided instead of a numeric variant id
                    // e.g. <friend-gift-handler data-keychain-sku="FRIEND-KEYCHAIN-GIFT"></friend-gift-handler>
                    this.keychainSku = (this.dataset.keychainSku || this.dataset.key || window.FRIEND_KEYCHAIN_SKU || null);

                    // `keychainProductId` was previously used as a fallback; keep supporting it
                    // but treat numeric values as potential variant IDs when a product handle lookup fails.
                    this.keychainProductId = this.dataset.keychainProductId || null; // Product ID fallback
                    this.keychainProductHandle = this.dataset.keychainProductHandle || 'friends-keychain-gift';
                    this.maxGifts = parseInt(this.dataset.maxGifts) || 500;
                    this.friendsCollectionHandles = ['friends', 'friend']; // Support multiple collection handles
                    this.friendTags = ['friends', 'friend']; // Tags to check (case-insensitive)
                    this.productCache = new Map(); // Cache product lookups
                    this.isInitialized = false;

                    console.log('🎁 Gift Handler Initializing:', {
                        variantId: this.keychainVariantId,
                        productId: this.keychainProductId,
                        handle: this.keychainProductHandle,
                        maxGifts: this.maxGifts,
                        friendsCollections: this.friendsCollectionHandles,
                        friendTags: this.friendTags
                    });

                    // Track recent cart additions to prevent duplicates
                    this.recentlyProcessed = new Set();
                    // Track the last clicked submit button for forms (helps detect Buy Now)
                    this._lastClickedSubmitButton = null;

                    // Subscribe to cart updates (for additions)
                    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
                        if (event.source === 'product-form') {
                            this.handleCartUpdate(event);
                        }
                        // Always check if gift should be removed when cart changes
                        this.checkGiftRemoval();
                    });
                }

                connectedCallback() {
                    this.init();
                }

                async init() {
                    // If variant ID not provided, fetch it from product handle
                    if (!this.keychainVariantId && this.keychainProductHandle) {
                        console.log('🔍 Fetching variant ID for product:', this.keychainProductHandle);
                        await this.fetchKeychainVariantId();
                    }

                    // If still no variant id, try interpreting `keychainProductId` as a variant id
                    if (!this.keychainVariantId && this.keychainProductId) {
                        const maybeId = this.keychainProductId.toString();
                        if (/^\d+$/.test(maybeId)) {
                            console.warn('⚠️ Using provided `data-keychain-product-id` value as variant id fallback:', maybeId);
                            this.keychainVariantId = maybeId;
                        }
                    }

                    if (!this.keychainVariantId) {
                        console.warn('❌ Cannot initialize: No variant ID found. You can provide `data-keychain-variant-id` on the <friend-gift-handler> element.');
                        // Keep element alive; user can call `setKeychainVariantId` later to enable functionality.
                        return;
                    }

                    this.isInitialized = true;
                    console.log('✅ Gift Handler Ready - Variant ID:', this.keychainVariantId);

                    // Check gift availability
                    this.checkGiftAvailability();

                    // Track clicks on submit buttons so we can detect Buy Now (non-AJAX) intents
                    document.addEventListener('click', (ev) => {
                        try {
                            const btn = ev.target.closest && ev.target.closest('button[type="submit"], input[type="submit"]');
                            if (btn && btn.form && btn.form.matches('form[action*="/cart/add"]')) {
                                this._lastClickedSubmitButton = btn;
                            }
                        } catch (e) {
                            // ignore
                        }
                    }, true);

                    // Intercept non-AJAX form submits for /cart/add so we can ensure the gift is added
                    document.addEventListener('submit', async (e) => {
                        const form = e.target;
                        if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;

                        // Best-effort: prevent native submit, perform AJAX add then add gift, then continue to checkout if "buy now"
                        e.preventDefault();

                        try {
                            const formData = new FormData(form);

                            // Include clicked button value if present
                            const btn = this._lastClickedSubmitButton;
                            if (btn && btn.name) {
                                // If button had a value, append it so server sees it
                                formData.append(btn.name, btn.value || '');
                            }

                            const config = fetchConfig('javascript');
                            config.headers['X-Requested-With'] = 'XMLHttpRequest';
                            delete config.headers['Content-Type'];
                            config.body = formData;

                            // Perform the product add via AJAX (mimics normal add-to-cart)
                            const addResponse = await fetch(routes.cart_add_url, config);
                            const addResult = addResponse.ok ? await addResponse.json().catch(() => null) : null;

                            // After product add, fetch fresh cart and ensure gift is present (capped to 1)
                            try {
                                const cartResponse = await fetch('/cart.js');
                                const freshCartData = await cartResponse.json();
                                await this.addKeychainToCart(freshCartData);
                            } catch (err) {
                                console.error('Error fetching cart after form add:', err);
                            }

                            // If this was a Buy Now / checkout intent, forward user to checkout
                            const isBuyNow = btn && (
                                btn.name === 'checkout' ||
                                btn.dataset?.buyNow !== undefined ||
                                btn.classList?.contains('buy-now') ||
                                btn.dataset?.action === 'buy-now' ||
                                btn.hasAttribute && btn.hasAttribute('data-buy-now')
                            );

                            if (isBuyNow) {
                                // Redirect to checkout
                                window.location.href = '/checkout';
                                return;
                            }

                            // Otherwise, if addResult contains a cart HTML or JSON we can trigger PubSub
                            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'friend-gift-handler', cartData: addResult });

                            // Reset last clicked button after processing
                            this._lastClickedSubmitButton = null;
                        } catch (error) {
                            console.error('Error handling non-AJAX add-to-cart submit:', error);
                            // Fall back to native submit so user action isn't blocked
                            try {
                                form.submit();
                            } catch (e) {
                                // ignore
                            }
                        }
                    }, true);

                    // Intercept AJAX cart adds
                    this.interceptFetch();

                    // Lock gift quantity controls in cart UI
                    this.lockGiftQuantityControls();

                    // Monitor for quantity manipulation attempts
                    this.monitorGiftQuantity();

                    console.log('✅ Gift handler active - watching for Friends products');
                }

                async fetchKeychainVariantId() {
                    try {
                        const response = await fetch(`/products/${this.keychainProductHandle}.js`);

                        if (!response.ok) {
                            console.warn(`⚠️ Keychain product not found by handle: ${this.keychainProductHandle}`);

                            // If caller provided a numeric `keychainProductId`, treat it as a variant id fallback
                            if (this.keychainProductId && /^\d+$/.test(this.keychainProductId.toString())) {
                                console.warn('⚠️ Falling back to provided `data-keychain-product-id` as variant id:', this.keychainProductId);
                                this.keychainVariantId = this.keychainProductId.toString();
                                return;
                            }

                            return;
                        }

                        const product = await response.json();

                        if (product.variants && product.variants.length > 0) {
                            this.keychainVariantId = product.variants[0].id.toString();
                            console.log('✅ Found variant ID by handle:', this.keychainVariantId);
                            console.log('📦 Product:', product.title);
                            console.log('💰 Price:', product.variants[0].price);
                            console.log('📊 Stock:', product.variants[0].inventory_quantity);
                            return;
                        }

                        // If we didn't find a variant via handle, try finding by SKU if available
                        if (this.keychainSku) {
                            console.log('🔁 Attempting SKU lookup for keychain variant:', this.keychainSku);
                            const skuId = await this.fetchVariantIdBySku(this.keychainSku);
                            if (skuId) {
                                this.keychainVariantId = skuId.toString();
                                console.log('✅ Found variant ID by SKU lookup:', this.keychainVariantId);
                                return;
                            }
                        }

                        console.error('❌ No variants found for product and SKU lookup failed');
                    } catch (error) {
                        console.error('❌ Error fetching variant ID:', error);
                    }
                }

                /**
                 * Public helper: allow other code (or theme) to set the variant id at runtime.
                 * Call `document.querySelector('friend-gift-handler').setKeychainVariantId('1234567890')`
                 */
                setKeychainVariantId(variantId) {
                    if (!variantId) return;
                    this.keychainVariantId = variantId.toString();
                    console.log('🔧 Keychain variant id set manually:', this.keychainVariantId);

                    // If initialization was previously blocked due to missing variant id, re-run init steps
                    if (!this.isInitialized) {
                        // run availability check and necessary setup
                        this.isInitialized = true;
                        this.checkGiftAvailability();
                        this.interceptFetch();
                        this.lockGiftQuantityControls();
                        this.monitorGiftQuantity();
                        console.log('✅ Gift Handler enabled after manual variant id set');
                    }
                }

                disconnectedCallback() {
                    if (this.cartUpdateUnsubscriber) {
                        this.cartUpdateUnsubscriber();
                    }
                }

                /**
                 * Check if gift is still available by checking keychain inventory
                 */
                async checkGiftAvailability() {
                    try {
                        if (!this.keychainVariantId) {
                            console.warn('Keychain variant ID not set');
                            return;
                        }

                        const response = await fetch(`/products/${this.keychainProductHandle}.js`);
                        if (!response.ok) {
                            console.warn(`Keychain product not found: ${this.keychainProductHandle}`);
                            return;
                        }

                        const product = await response.json();

                        if (product && product.variants) {
                            const keychainVariant = product.variants.find(v => v.id == this.keychainVariantId);
                            if (keychainVariant) {
                                const remainingGifts = keychainVariant.inventory_quantity || 0;
                                console.log('📊 Remaining gifts:', remainingGifts);
                                this.updateAvailabilityDisplay(remainingGifts);
                            }
                        }
                    } catch (error) {
                        console.error('Error checking gift availability:', error);
                    }
                }

                /**
                 * Handle cart update event from Friends products
                 */
                async handleCartUpdate(event) {
                    if (!this.isInitialized) {
                        console.log('⏳ Gift handler not ready yet, skipping...');
                        return;
                    }

                    const variantId = event.productVariantId?.toString();
                    if (!variantId) return;

                    // Prevent duplicate processing within 2 seconds
                    if (this.recentlyProcessed.has(variantId)) {
                        console.log('⏭️  Already processed this variant recently, skipping');
                        return;
                    }

                    // Check if the added product is in the Friends collection
                    const isFriendProduct = await this.isFriendsProduct(variantId);

                    if (isFriendProduct) {
                        console.log('✅ Friends product confirmed - adding gift');

                        // Mark as processed to prevent duplicates
                        this.recentlyProcessed.add(variantId);
                        setTimeout(() => this.recentlyProcessed.delete(variantId), 2000);

                        // Fetch fresh cart data to get accurate item count
                        try {
                            const cartResponse = await fetch('/cart.js');
                            const freshCartData = await cartResponse.json();
                            await this.addKeychainToCart(freshCartData);
                        } catch (error) {
                            console.error('Error fetching cart data:', error);
                        }
                    }
                }

                /**
                 * Check if a product variant belongs to the Friends collection
                 */
                async isFriendsProduct(variantId) {
                    // Check cache first
                    if (this.productCache.has(variantId)) {
                        const cached = this.productCache.get(variantId);
                        console.log('📋 Using cached result for variant:', variantId, '→', cached);
                        return cached;
                    }

                    try {
                        // METHOD 1: Check current URL for Friends collection
                        const currentPath = window.location.pathname.toLowerCase();
                        const isOnFriendsCollection = this.friendsCollectionHandles.some(handle =>
                            currentPath.includes(`/collections/${handle}`)
                        );

                        if (isOnFriendsCollection) {
                            console.log('✅ On Friends collection page - auto-qualify');
                            this.productCache.set(variantId, true);
                            return true;
                        }

                        // METHOD 2: Fetch product data and check tags/collections
                        console.log('🔍 Fetching product data for variant:', variantId);

                        const productData = await this.fetchProductByVariant(variantId);

                        if (!productData) {
                            console.log('⚠️ Product not found');
                            this.productCache.set(variantId, false);
                            return false;
                        }

                        console.log('📦 Product found:', productData.title);
                        console.log('🏷️  Product tags:', productData.tags);

                        // Check if product has any of the Friends tags (case-insensitive)
                        const hasFriendsTag = productData.tags.some(tag =>
                            this.friendTags.some(friendTag =>
                                tag.toLowerCase() === friendTag.toLowerCase()
                            )
                        );

                        console.log('Has Friends tag?', hasFriendsTag);

                        // Cache result
                        this.productCache.set(variantId, hasFriendsTag);

                        return hasFriendsTag;

                    } catch (error) {
                        console.error('❌ Error checking product:', error);
                        this.productCache.set(variantId, false);
                        return false;
                    }
                }

                async fetchProductByVariant(variantId) {
                    try {
                        // Try to get product from collection page first (faster)
                        const collectionProducts = await this.getCollectionProducts();

                        for (const product of collectionProducts) {
                            const variant = product.variants.find(v =>
                                v.id.toString() === variantId.toString()
                            );
                            if (variant) {
                                return product;
                            }
                        }

                        // Fallback: Fetch all products (slower but comprehensive)
                        const response = await fetch('/products.json?limit=250');
                        if (!response.ok) return null;

                        const data = await response.json();

                        for (const product of data.products) {
                            const variant = product.variants.find(v =>
                                v.id.toString() === variantId.toString()
                            );
                            if (variant) {
                                return product;
                            }
                        }

                        return null;

                    } catch (error) {
                        console.error('❌ Error fetching product:', error);
                        return null;
                    }
                }

                async getCollectionProducts() {
                    try {
                        // If on a collection page, get products from current collection
                        const currentPath = window.location.pathname;
                        const collectionMatch = currentPath.match(/\/collections\/([^\/]+)/);

                        if (collectionMatch) {
                            const collectionHandle = collectionMatch[1];
                            const response = await fetch(`/collections/${collectionHandle}/products.json?limit=250`);

                            if (response.ok) {
                                const data = await response.json();
                                return data.products || [];
                            }
                        }

                        return [];

                    } catch (error) {
                        console.log('ℹ️ Could not fetch collection products');
                        return [];
                    }
                }

                /**
                 * Add free keychain to cart (or update quantity)
                 */
                async addKeychainToCart(cartData) {
                    if (!this.keychainVariantId) {
                        console.warn('Cannot add keychain: variant ID not configured');
                        return;
                    }

                    try {
                        // Count how many Friends products are in cart
                        let friendsProductCount = 0;

                        for (const item of cartData.items || []) {
                            // Skip the gift itself
                            if (item.variant_id.toString() === this.keychainVariantId.toString()) {
                                continue;
                            }

                            // Check if this is a Friends product
                            const isFriends = await this.isFriendsProduct(item.variant_id);
                            if (isFriends) {
                                friendsProductCount += item.quantity; // Count each quantity
                            }
                        }

                        console.log('📦 Friends products in cart:', friendsProductCount);

                        // We cap the free keychain to a single item per cart/receipt
                        const giftNeeded = friendsProductCount > 0 ? 1 : 0;

                        if (giftNeeded === 0) {
                            console.log('ℹ️ No Friends products - no gift needed');
                            return;
                        }

                        // Check if gift is already in cart
                        const existingGift = cartData.items?.find(item =>
                            item.variant_id.toString() === this.keychainVariantId.toString()
                        );

                        // Check stock
                        const stock = await this.checkGiftStock();
                        console.log('📊 Remaining gifts:', stock);

                        if (stock <= 0) {
                            console.log('❌ Gift promotion ended (sold out)');
                            this.showGiftNotification('Friends gift promotion has ended', 'warning');
                            return;
                        }

                        // If gift exists, ensure quantity is capped to 1
                        if (existingGift) {
                            const currentGiftQty = existingGift.quantity;

                            if (currentGiftQty === 1) {
                                console.log('ℹ️ Gift quantity already capped to 1');
                                return;
                            }

                            console.log(`🔄 Updating gift quantity: ${currentGiftQty} → 1`);
                            await this.updateGiftQuantity(existingGift.key, 1);
                            return;
                        }

                        // Gift not in cart - add it
                        const formData = new FormData();
                        formData.append('id', this.keychainVariantId);
                        // Always add only one keychain regardless of Friends items count
                        formData.append('quantity', '1');
                        formData.append('properties[_gift]', 'true');
                        formData.append('properties[Free Gift]', 'Friends Collection Keychain');

                        const config = fetchConfig('javascript');
                        config.headers['X-Requested-With'] = 'XMLHttpRequest';
                        delete config.headers['Content-Type'];
                        config.body = formData;

                        const response = await fetch(`${routes.cart_add_url}`, config);
                        const result = await response.json();

                        if (result.status !== 'error') {
                            console.log('✅ Gift added successfully with quantity:', friendsProductCount);

                            const remainingGifts = stock - friendsProductCount;
                            const message = friendsProductCount > 1
                                ? `${friendsProductCount} free Friends keychains added to your cart! 🎁`
                                : 'Free Friends keychain added to your cart! 🎁';

                            this.updateAvailabilityDisplay(remainingGifts);
                            this.showGiftNotification(message, 'success');

                            // Refresh cart display - use PubSub instead of direct rendering
                            publish(PUB_SUB_EVENTS.cartUpdate, {
                                source: 'friend-gift-handler',
                                cartData: result
                            });
                        }
                    } catch (error) {
                        console.error('Error adding keychain gift:', error);
                    }
                }

                /**
                 * Update gift quantity to match Friends products count
                 */
                async updateGiftQuantity(itemKey, newQuantity) {
                    try {
                        // Enforce max 1
                        const qty = Math.min(1, parseInt(newQuantity, 10) || 0);

                        const payload = { id: itemKey, quantity: qty };
                        const config = fetchConfig('javascript');
                        config.headers['X-Requested-With'] = 'XMLHttpRequest';
                        config.headers['Content-Type'] = 'application/json';
                        config.body = JSON.stringify(payload);

                        const response = await fetch(`${routes.cart_change_url}`, config);
                        const result = await response.json().catch(() => null);

                        if (result) {
                            console.log('✅ Gift quantity updated to:', newQuantity);

                            // Use PubSub to notify cart of update
                            publish(PUB_SUB_EVENTS.cartUpdate, {
                                source: 'friend-gift-handler'
                            });
                        }
                    } catch (error) {
                        console.error('Error updating gift quantity:', error);
                    }
                }

                async checkGiftStock() {
                    try {
                        const response = await fetch(`/products/${this.keychainProductHandle}.js`);

                        if (!response.ok) {
                            console.error(`❌ Keychain product not found: ${this.keychainProductHandle}`);
                            return 0;
                        }

                        const product = await response.json();
                        const variant = product.variants.find(v =>
                            v.id.toString() === this.keychainVariantId.toString()
                        );

                        if (!variant) {
                            console.error(`❌ Variant ${this.keychainVariantId} not found`);
                            return 0;
                        }

                        // Return available quantity
                        return variant.inventory_quantity || (variant.available ? 999 : 0);

                    } catch (error) {
                        console.error('❌ Stock check failed:', error);
                        return 0;
                    }
                }

                interceptFetch() {
                    const originalFetch = window.fetch;
                    const self = this;

                    // Map to dedupe in-flight /cart/add requests by serialized payload
                    const inFlightCartAdds = new Map(); // signature -> Promise<Response>
                    // Variant-level recent promises to aggressively dedupe rapid duplicate adds
                    const recentVariantPromises = new Map(); // variantId -> Promise<Response>

                    // Helper to serialize request bodies (FormData, URLSearchParams, JSON string)
                    function serializeBody(body) {
                        try {
                            if (!body) return '';

                            if (typeof body === 'string') return body;

                            if (body instanceof URLSearchParams) return body.toString();

                            if (typeof FormData !== 'undefined' && body instanceof FormData) {
                                const entries = [];
                                for (const pair of body.entries()) {
                                    entries.push(`${pair[0]}=${pair[1]}`);
                                }
                                entries.sort();
                                return entries.join('&');
                            }

                            // Try to stringify JSON bodies
                            if (typeof body === 'object') return JSON.stringify(body);

                            return String(body);
                        } catch (e) {
                            return '';
                        }
                    }

                    // Try to extract variant id (or id field) from common body types
                    function extractVariantIdFromBody(body) {
                        try {
                            if (!body) return null;

                            // FormData
                            if (typeof FormData !== 'undefined' && body instanceof FormData) {
                                if (body.has && body.has('id')) return String(body.get('id'));
                                if (body.get) return String(body.get('id') || body.get('items[]') || '');
                            }

                            // URLSearchParams
                            if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
                                if (body.has('id')) return String(body.get('id'));
                            }

                            // If body is a string like "id=123&quantity=1"
                            if (typeof body === 'string') {
                                const m = body.match(/(?:^|&)id=(\d+)/);
                                if (m) return m[1];
                                const m2 = body.match(/(?:^|&)items%5B%5D=(\d+)/); // items[] urlencoded
                                if (m2) return m2[1];
                            }

                            // JSON object
                            if (typeof body === 'object') {
                                if (body.id) return String(body.id);
                                if (body.items && Array.isArray(body.items) && body.items[0] && body.items[0].id) return String(body.items[0].id);
                            }

                            return null;
                        } catch (e) {
                            return null;
                        }
                    }

                    window.fetch = async function (...args) {
                        const [url, options] = args;

                        // Detect AJAX cart add requests
                        if (url && String(url).includes('/cart/add')) {
                            try {
                                // Compute signature based on URL+method+body
                                const method = (options && options.method) ? options.method.toUpperCase() : 'GET';
                                const body = options && options.body;
                                // Prefer variant-level signature when we can extract an id from the body
                                const variantId = extractVariantIdFromBody(body);
                                let signature;
                                if (variantId) {
                                    signature = `${url}|${method}|variant:${variantId}`;
                                } else {
                                    const bodySignature = serializeBody(body);
                                    signature = `${url}|${method}|${bodySignature}`;
                                }

                                if (variantId && recentVariantPromises.has(variantId)) {
                                    console.log(`🛡️ Variant-level dedupe: returning recent promise for variant ${variantId}`);
                                    return recentVariantPromises.get(variantId);
                                }

                                if (inFlightCartAdds.has(signature)) {
                                    // Return the existing in-flight promise so duplicate callers share the same request
                                    console.log('🧩 Deduping duplicate /cart/add request');
                                    return inFlightCartAdds.get(signature);
                                }

                                // Create a promise for this fetch and store it
                                const fetchPromise = originalFetch.apply(this, args)
                                    .then(async (res) => {
                                        // Clean up stored promise shortly after completion
                                        setTimeout(() => inFlightCartAdds.delete(signature), 1000);
                                        return res;
                                    })
                                    .catch((err) => {
                                        inFlightCartAdds.delete(signature);
                                        throw err;
                                    });

                                inFlightCartAdds.set(signature, fetchPromise);
                                if (variantId) {
                                    // Store a variant-level promise for a short window to ignore rapid duplicate adds
                                    recentVariantPromises.set(variantId, fetchPromise);
                                    setTimeout(() => recentVariantPromises.delete(variantId), 500);
                                }

                                const result = await fetchPromise;

                                // Only process if it was a successful add
                                if (result && result.ok) {
                                    const clone = result.clone();

                                    try {
                                        const data = await clone.json();
                                        const variantId = (data.id || data.variant_id || data.items?.[0]?.variant_id)?.toString();

                                        if (variantId) {
                                            console.log('🔍 AJAX added variant:', variantId);

                                            // Prevent duplicate processing
                                            if (self.recentlyProcessed.has(variantId)) {
                                                console.log('⏭️  AJAX: Already processed this variant, skipping');
                                                return result;
                                            }

                                            const isFriends = await self.isFriendsProduct(variantId);
                                            if (isFriends) {
                                                console.log('✅ AJAX Friends product - adding gift');

                                                // Mark as processed
                                                self.recentlyProcessed.add(variantId);
                                                setTimeout(() => self.recentlyProcessed.delete(variantId), 2000);

                                                // Fetch fresh cart data before adding gift
                                                setTimeout(async () => {
                                                    try {
                                                        const cartResponse = await fetch('/cart.js');
                                                        const freshCartData = await cartResponse.json();
                                                        await self.addKeychainToCart(freshCartData);
                                                    } catch (error) {
                                                        console.error('Error fetching cart for gift:', error);
                                                    }
                                                }, 800);
                                            }
                                        }
                                    } catch (e) {
                                        console.log('ℹ️ Could not parse cart response');
                                    }
                                }

                                return result;
                            } catch (outerErr) {
                                console.error('Error in fetch interceptor for /cart/add:', outerErr);
                                return originalFetch.apply(this, args);
                            }
                        }

                        return originalFetch.apply(this, args);
                    };
                }

                /**
                 * Update availability display on page
                 */
                updateAvailabilityDisplay(remainingGifts) {
                    const displayElement = document.querySelector('.friend-gift-availability');
                    if (displayElement) {
                        if (remainingGifts > 0) {
                            displayElement.innerHTML = `
              <div class="gift-available">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M20 12v10H4V12M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
                </svg>
                <span>Free keychain gift with purchase! <strong>${remainingGifts} remaining</strong></span>
              </div>
            `;
                            displayElement.style.display = 'block';
                        } else {
                            displayElement.innerHTML = `
              <div class="gift-unavailable">
                <span>Free keychain promotion has ended</span>
              </div>
            `;
                            displayElement.style.display = 'block';
                        }
                    }
                }

                /**
                 * Lock quantity controls for gift items in cart
                 * Makes the keychain quantity non-editable but allows delete
                 */
                lockGiftQuantityControls() {
                    // Run immediately and on cart updates
                    const lockControls = () => {
                        if (!this.keychainVariantId) return;

                        // Find quantity input for keychain variant
                        const quantityInput = document.querySelector(`input[data-quantity-variant-id="${this.keychainVariantId}"]`);

                        if (quantityInput) {
                            // Disable the input (prevents editing)
                            quantityInput.disabled = true;
                            quantityInput.readOnly = true;

                            // Hide +/- buttons via CSS (already handled in CSS)
                            // Delete button remains functional - no need to touch it

                            console.log('🔒 Locked gift quantity controls (delete still works)');
                        }
                    };

                    // Lock now
                    lockControls();

                    // Re-lock when cart UI updates (drawer opens, cart page loads)
                    const observer = new MutationObserver(() => {
                        lockControls();
                    });

                    // Watch for cart drawer and cart page changes
                    const cartDrawer = document.querySelector('cart-drawer');
                    const cartItems = document.querySelector('cart-items');

                    if (cartDrawer) {
                        observer.observe(cartDrawer, { childList: true, subtree: true });
                    }
                    if (cartItems) {
                        observer.observe(cartItems, { childList: true, subtree: true });
                    }
                }

                /**
                 * Check if gift should be removed when Friends products are removed
                 */
                async checkGiftRemoval() {
                    try {
                        const cartResponse = await fetch('/cart.js');
                        const cartData = await cartResponse.json();

                        if (!cartData.items || cartData.items.length === 0) {
                            return; // Empty cart, nothing to check
                        }

                        // Find all keychain gift line items in cart
                        const keychainItems = (cartData.items || []).filter(item =>
                            item.variant_id.toString() === this.keychainVariantId?.toString()
                        );

                        if (!keychainItems || keychainItems.length === 0) {
                            return; // No gift in cart, nothing to remove
                        }

                        // Check if there are any Friends products in cart (excluding the gift itself)
                        let hasFriendsProduct = false;

                        for (const item of cartData.items) {
                            // Skip any gift keychain items
                            if (item.variant_id.toString() === this.keychainVariantId?.toString()) {
                                continue;
                            }

                            // Check if this item is a Friends product
                            const isFriends = await this.isFriendsProduct(item.variant_id);
                            if (isFriends) {
                                hasFriendsProduct = true;
                                break;
                            }
                        }

                        // If no Friends products remain, remove ALL keychain gift line items and reload once
                        if (!hasFriendsProduct) {
                            console.log('🗑️  No Friends products in cart - removing all gift line items');
                            for (const kc of keychainItems) {
                                try {
                                    await this.removeKeychainFromCart(kc.key, { suppressReload: true });
                                } catch (err) {
                                    console.error('Error removing gift line item during cleanup:', err);
                                }
                            }

                            // After batch removal, refresh UI once
                            setTimeout(() => window.location.reload(), 1200);
                        }

                    } catch (error) {
                        console.log('ℹ️ Could not check gift removal:', error.message);
                    }
                }

                /**
                 * Remove keychain gift from cart
                 */
                async removeKeychainFromCart(itemKey, { suppressReload = false } = {}) {
                    try {
                        const payload = { id: itemKey, quantity: 0 };
                        const config = fetchConfig('javascript');
                        config.headers['X-Requested-With'] = 'XMLHttpRequest';
                        config.headers['Content-Type'] = 'application/json';
                        config.body = JSON.stringify(payload);

                        const response = await fetch(`${routes.cart_change_url}`, config);
                        const result = await response.json().catch(() => null);

                        if (result) {
                            console.log('✅ Gift removed from cart');
                            this.showGiftNotification('Free gift removed (no Friends products in cart)', 'warning');

                            if (!suppressReload) {
                                // Force full page reload to refresh cart UI
                                // This ensures all cart displays are updated (drawer, notification, cart page)
                                setTimeout(() => {
                                    window.location.reload();
                                }, 1500); // Give user time to see the notification
                            }
                        }
                    } catch (error) {
                        console.error('Error removing gift:', error);
                    }
                }

                /**
                 * Monitor gift quantity for manipulation attempts
                 * Prevents users from manually changing quantity to more than they should get
                 */
                monitorGiftQuantity() {
                    if (!this.keychainVariantId) return;

                    // Monitor cart changes every 3 seconds
                    setInterval(async () => {
                        try {
                            const cartResponse = await fetch('/cart.js');
                            const cartData = await cartResponse.json();

                            const giftItem = cartData.items?.find(item =>
                                item.variant_id.toString() === this.keychainVariantId?.toString()
                            );

                            if (giftItem) {
                                // Count Friends products in cart
                                let friendsProductCount = 0;

                                for (const item of cartData.items) {
                                    if (item.variant_id.toString() === this.keychainVariantId.toString()) {
                                        continue; // Skip gift itself
                                    }

                                    const isFriends = await this.isFriendsProduct(item.variant_id);
                                    if (isFriends) {
                                        friendsProductCount += item.quantity;
                                    }
                                }

                                // Gift quantity should be capped to 1 if any Friends products exist
                                const correctQuantity = friendsProductCount > 0 ? 1 : 0;

                                if (giftItem.quantity !== correctQuantity) {
                                    console.warn(`⚠️  Gift quantity mismatch! Expected: ${correctQuantity}, Got: ${giftItem.quantity}`);

                                    if (correctQuantity === 0) {
                                        // No Friends products - remove gift (suppress reload; monitor will handle UI)
                                        await this.removeKeychainFromCart(giftItem.key, { suppressReload: true });
                                    } else {
                                        // Fix quantity to 1
                                        await this.updateGiftQuantity(giftItem.key, 1);
                                    }
                                }
                            }
                        } catch (error) {
                            console.log('ℹ️ Could not monitor gift quantity:', error.message);
                        }
                    }, 3000); // Check every 3 seconds
                }

                /**
                 * Show notification that gift was added
                 */
                showGiftNotification(message, type = 'success') {
                    console.log(`📢 ${type.toUpperCase()}:`, message);

                    // Remove any existing notifications
                    const existing = document.querySelector('.friend-gift-notification');
                    if (existing) existing.remove();

                    const notification = document.createElement('div');
                    notification.className = `friend-gift-notification friend-gift-notification--${type}`;
                    notification.innerHTML = `
          <div class="friend-gift-notification__content">
            <span class="friend-gift-notification__icon">${type === 'success' ? '✓' : '⚠'}</span>
            <span class="friend-gift-notification__message">${message}</span>
          </div>
        `;

                    document.body.appendChild(notification);

                    setTimeout(() => {
                        notification.classList.add('show');
                    }, 100);

                    setTimeout(() => {
                        notification.classList.remove('show');
                        setTimeout(() => notification.remove(), 300);
                    }, 4000);
                }

                /**
                 * Search up to 250 products and return the first variant id that matches the given SKU.
                 * Returns variant id string or null.
                 */
                async fetchVariantIdBySku(sku) {
                    if (!sku) return null;

                    try {
                        const resp = await fetch('/products.json?limit=250');
                        if (!resp.ok) return null;
                        const data = await resp.json();

                        for (const product of data.products || []) {
                            for (const v of product.variants || []) {
                                if ((v.sku || '').toString().toLowerCase() === sku.toString().toLowerCase()) {
                                    return v.id.toString();
                                }
                            }
                        }

                        return null;
                    } catch (err) {
                        console.error('❌ Error during SKU variant lookup:', err);
                        return null;
                    }
                }
            }
        );
    }

    // End disable guard wrapper


}

/* Fallback helper: add `.friend-hide-quantity` to quantity containers for the
   configured gift variant when the browser doesn't support `:has()` or when
   you want a JS-driven toggle. This runs independently of the FriendGiftHandler
   custom element (so it still works when `window.DISABLE_FRIEND_GIFT_HANDLER = true`). */
(function lockGiftQuantityFallback() {
    const VARIANT_ID = '45880443502745';

    function applyLock() {
        try {
            const selector = `input[data-quantity-variant-id="${VARIANT_ID}"]`;
            const inputs = document.querySelectorAll(selector);
            inputs.forEach((input) => {
                // Prefer the popover wrapper
                const popover = input.closest('.quantity-popover-container') || input.closest('quantity-input') || input.parentElement;
                if (popover && !popover.classList.contains('friend-lock-quantity')) {
                    popover.classList.add('friend-lock-quantity');
                }

                // Make the input read-only so keyboard edits can't change it
                try {
                    input.readOnly = true;
                    input.setAttribute('aria-readonly', 'true');
                } catch (e) {
                    // ignore
                }

                // Disable +/- buttons if present
                const buttons = popover ? popover.querySelectorAll('button.quantity__button, .quantity__button') : [];
                buttons.forEach((btn) => {
                    try { btn.disabled = true; btn.setAttribute('aria-hidden', 'true'); } catch (e) { }
                });
            });
        } catch (err) {
            console.error('Friend Gift: lock fallback error', err);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', applyLock);
    } else {
        applyLock();
    }

    // Observe DOM changes in case cart is updated dynamically
    try {
        const mo = new MutationObserver((mutations) => {
            for (const m of mutations) {
                if (m.addedNodes && m.addedNodes.length) {
                    applyLock();
                    break;
                }
            }
        });
        mo.observe(document.body, { childList: true, subtree: true });
    } catch (e) {
        // ignore
    }
})();